import { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { parseUTCTimestamp } from '@/utils/formatters'
import { Check, Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  fetchHierarchyTree,
  type HierarchyTreeNode,
} from '@/api/productHierarchy'
import { queryKeys } from '@/lib/queryKeys'
import { usePlanCalendar } from '@/hooks/usePlanCalendar'
import { usePogGroups } from '@/hooks/useStoreAttributes'
import { CalendarEventPickerDialog } from './CalendarEventPickerDialog'
import { cn } from '@/utils/cn'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import type { ApiPlanDetailResponse } from '@/types'

// --- Option types for select fields ---

interface SelectOption {
  value: string
  label: string
}

// --- Field config types ---

type HierarchyLevel = 'DIVISION' | 'DEPARTMENT' | 'CLASS' | 'SUBCLASS'

type FieldType =
  | 'text'
  | 'date'
  | 'float'
  | 'percent'
  | 'integer'
  | 'select'
  | 'hierarchy'
  | 'season'
  | 'pogGroup'

interface BaseFieldConfig {
  label: string
  key: string
  getValue: (plan: ApiPlanDetailResponse) => string | number | null
  editable: boolean
  type: FieldType
}

interface SelectFieldConfig extends BaseFieldConfig {
  type: 'select'
  options: SelectOption[]
}

interface HierarchyFieldConfig extends BaseFieldConfig {
  type: 'hierarchy'
  hierarchyLevel: HierarchyLevel
}

interface SeasonFieldConfig extends BaseFieldConfig {
  type: 'season'
}

type FieldConfig =
  | BaseFieldConfig
  | SelectFieldConfig
  | HierarchyFieldConfig
  | SeasonFieldConfig

// --- Dropdown option definitions ---

const STRATEGIC_ROLE_OPTIONS: SelectOption[] = [
  { value: 'PRIMARY_BUSINESS_DRIVER', label: 'Primary Business Driver' },
  { value: 'TRAFFIC_DRIVER', label: 'Traffic Driver' },
  { value: 'PROFIT_GENERATOR', label: 'Profit Generator' },
  { value: 'CONVENIENCE', label: 'Convenience' },
  { value: 'EMERGING_BUSINESS', label: 'Emerging Business' },
]

const STRATEGIC_INTENT_OPTIONS: SelectOption[] = [
  { value: 'AGGRESSIVE_GROW', label: 'Aggressive Grow' },
  { value: 'GROW', label: 'Grow' },
  { value: 'MAINTAIN', label: 'Maintain' },
  { value: 'HARVEST_OPTIMIZE', label: 'Harvest Optimize' },
  { value: 'EXIT', label: 'Exit' },
]

// --- Field configuration ---

const FIELD_CONFIG: FieldConfig[] = [
  {
    label: 'Division',
    key: 'division',
    getValue: plan =>
      plan.hierarchy?.lineage?.find(l => l.level === 'DIVISION')?.name ??
      (plan.hierarchy?.level === 'DIVISION' ? plan.hierarchy.name : null),
    editable: true,
    type: 'hierarchy',
    hierarchyLevel: 'DIVISION',
  },
  {
    label: 'Department',
    key: 'department',
    getValue: plan =>
      plan.hierarchy?.lineage?.find(l => l.level === 'DEPARTMENT')?.name ??
      (plan.hierarchy?.level === 'DEPARTMENT' ? plan.hierarchy.name : null),
    editable: true,
    type: 'hierarchy',
    hierarchyLevel: 'DEPARTMENT',
  },
  {
    label: 'Class',
    key: 'class',
    getValue: plan =>
      plan.hierarchy?.lineage?.find(l => l.level === 'CLASS')?.name ??
      (plan.hierarchy?.level === 'CLASS' ? plan.hierarchy.name : null),
    editable: true,
    type: 'hierarchy',
    hierarchyLevel: 'CLASS',
  },
  {
    label: 'SubClass',
    key: 'subclass',
    getValue: plan =>
      plan.hierarchy?.lineage?.find(l => l.level === 'SUBCLASS')?.name ??
      (plan.hierarchy?.level === 'SUBCLASS' ? plan.hierarchy.name : null),
    editable: true,
    type: 'hierarchy',
    hierarchyLevel: 'SUBCLASS',
  },
  {
    label: 'Name',
    key: 'name',
    getValue: plan => plan.name,
    editable: true,
    type: 'text',
  },
  {
    label: 'Season',
    key: 'season',
    getValue: plan => plan.season?.name ?? null,
    editable: true,
    type: 'season',
  },
  {
    label: 'Set/Reset Date',
    key: 'set_reset_date',
    getValue: plan => plan.set_reset_date,
    editable: true,
    type: 'date',
  },
  {
    label: 'Exit Date',
    key: 'exit_date',
    getValue: plan => plan.exit_date,
    editable: true,
    type: 'date',
  },
  {
    label: 'In-Season ST%',
    key: 'planned_st_pct',
    getValue: plan => plan.planned_st_pct,
    editable: true,
    type: 'percent',
  },
  {
    label: 'End of Season ST%',
    key: 'end_of_season_st_pct',
    getValue: plan => plan.end_of_season_st_pct,
    editable: true,
    type: 'percent',
  },
  {
    label: 'Discount Rate',
    key: 'discount_rate',
    getValue: plan => plan.discount_rate,
    editable: true,
    type: 'percent',
  },
  {
    label: 'WOS Goal',
    key: 'target_wos',
    getValue: plan => plan.target_wos,
    editable: true,
    type: 'float',
  },
  {
    label: 'New Store Default %',
    key: 'new_store_default_pct',
    getValue: plan => plan.new_store_default_pct,
    editable: true,
    type: 'percent',
  },
  {
    label: 'Strategic Role',
    key: 'strategic_role',
    getValue: plan => plan.strategic_role,
    editable: true,
    type: 'select',
    options: STRATEGIC_ROLE_OPTIONS,
  },
  {
    label: 'Strategic Intent',
    key: 'strategic_intent',
    getValue: plan => plan.strategic_intent,
    editable: true,
    type: 'select',
    options: STRATEGIC_INTENT_OPTIONS,
  },
  {
    label: 'POG Group',
    key: 'pog_groups_involved',
    getValue: plan => plan.pog_groups_involved,
    editable: true,
    type: 'pogGroup',
  },
  {
    label: 'POGs Involved',
    key: 'pogs_involved',
    getValue: plan => (plan.pogs_involved != null ? plan.pogs_involved : null),
    editable: true,
    type: 'integer',
  },
  {
    label: 'Feature Locations',
    key: 'feature_locations',
    getValue: plan => plan.feature_locations,
    editable: true,
    type: 'text',
  },
]

// --- Shared edit input styles ---

const EDIT_INPUT_CLASS =
  'h-7 border-0 border-b-2 border-blue-500 rounded-none px-1 text-sm font-medium focus-visible:ring-0 focus-visible:ring-offset-0'

const READ_VALUE_CLASS =
  'cursor-pointer text-sm font-medium min-h-[1.75rem] flex items-center px-1'

// --- Helpers ---

function formatDisplayValue(
  value: string | number | null,
  type: FieldType,
  field: FieldConfig
): string {
  if (value == null || value === '') return '-'

  switch (type) {
    case 'date': {
      try {
        return format(parseUTCTimestamp(String(value)), 'MMM d, yyyy')
      } catch {
        return String(value)
      }
    }
    case 'percent':
      return `${value}%`
    case 'select': {
      const selectField = field as SelectFieldConfig
      const option = selectField.options.find(o => o.value === value)
      return option?.label ?? String(value)
    }
    default:
      return String(value)
  }
}

// --- EditableTextField ---

interface EditableTextFieldProps {
  value: string | null
  isEditing: boolean
  isSaving?: boolean
  onStartEdit: () => void
  onSave: (value: string | null) => void
  onCancel: () => void
}

function EditableTextField({
  value,
  isEditing,
  isSaving,
  onStartEdit,
  onSave,
  onCancel,
}: EditableTextFieldProps) {
  const [editValue, setEditValue] = useState(value || '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isEditing) setEditValue(value || '')
  }, [value, isEditing])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = () => {
    const trimmed = editValue.trim()
    if (trimmed !== (value || '')) {
      onSave(trimmed || null)
    } else {
      onCancel()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setEditValue(value || '')
      onCancel()
    }
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleSave}
        className={EDIT_INPUT_CLASS}
      />
    )
  }

  return (
    <p
      onDoubleClick={isSaving ? undefined : onStartEdit}
      className={READ_VALUE_CLASS}
    >
      {value || '-'}
      {isSaving && (
        <Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </p>
  )
}

// --- EditableNumberField ---

interface EditableNumberFieldProps {
  value: string | number | null
  isEditing: boolean
  isSaving?: boolean
  onStartEdit: () => void
  onSave: (value: number | null) => void
  onCancel: () => void
  step: string
  isPercent?: boolean
}

function EditableNumberField({
  value,
  isEditing,
  isSaving,
  onStartEdit,
  onSave,
  onCancel,
  step,
  isPercent,
}: EditableNumberFieldProps) {
  const [editValue, setEditValue] = useState(value != null ? String(value) : '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isEditing) setEditValue(value != null ? String(value) : '')
  }, [value, isEditing])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = () => {
    if (editValue.trim() === '') {
      if (value != null) onSave(null)
      else onCancel()
      return
    }
    const parsed =
      step === '1' ? parseInt(editValue, 10) : parseFloat(editValue)
    if (isNaN(parsed)) {
      onCancel()
      return
    }
    if (String(parsed) !== String(value)) {
      onSave(parsed)
    } else {
      onCancel()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setEditValue(value != null ? String(value) : '')
      onCancel()
    }
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        type="number"
        step={step}
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleSave}
        className={EDIT_INPUT_CLASS}
      />
    )
  }

  const displayValue =
    value != null && value !== '' ? `${value}${isPercent ? '%' : ''}` : '-'

  return (
    <p
      onDoubleClick={isSaving ? undefined : onStartEdit}
      className={READ_VALUE_CLASS}
    >
      {displayValue}
      {isSaving && (
        <Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </p>
  )
}

// --- EditableSelectField ---

interface EditableSelectFieldProps {
  value: string | null
  isEditing: boolean
  isSaving?: boolean
  onStartEdit: () => void
  onSave: (value: string | null) => void
  onCancel: () => void
  options: SelectOption[]
  isLoadingOptions?: boolean
  allowClear?: boolean
}

const CLEAR_SENTINEL = '__none__'

function EditableSelectField({
  value,
  isEditing,
  isSaving,
  onStartEdit,
  onSave,
  onCancel,
  options,
  isLoadingOptions,
  allowClear,
}: EditableSelectFieldProps) {
  const displayLabel =
    options.find(o => o.value === value)?.label ?? value ?? '-'

  if (isEditing) {
    return (
      <Select
        defaultOpen
        value={value ?? undefined}
        onValueChange={newValue => {
          if (newValue === CLEAR_SENTINEL) {
            if (value != null) onSave(null)
            else onCancel()
          } else if (newValue !== value) {
            onSave(newValue)
          } else {
            onCancel()
          }
        }}
        onOpenChange={open => {
          if (!open) onCancel()
        }}
      >
        <SelectTrigger className="h-7 border-0 border-b-2 border-blue-500 rounded-none px-1 text-sm font-medium focus:ring-0 focus:ring-offset-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {isLoadingOptions && (
            <SelectItem value="__loading__" disabled>
              Loading...
            </SelectItem>
          )}
          {allowClear && value != null && (
            <SelectItem value={CLEAR_SENTINEL}>None</SelectItem>
          )}
          {options.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  return (
    <p
      onDoubleClick={isSaving ? undefined : onStartEdit}
      className={READ_VALUE_CLASS}
    >
      {displayLabel}
      {isSaving && (
        <Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </p>
  )
}

// --- EditableDateField ---

interface EditableDateFieldProps {
  value: string | null
  isEditing: boolean
  isSaving?: boolean
  onStartEdit: () => void
  onSave: (value: string | null) => void
  onCancel: () => void
}

function EditableDateField({
  value,
  isEditing,
  isSaving,
  onStartEdit,
  onSave,
  onCancel,
}: EditableDateFieldProps) {
  const selectedDate = value ? parseUTCTimestamp(value) : undefined

  const displayValue = value
    ? (() => {
        try {
          return format(parseUTCTimestamp(value), 'MMM d, yyyy')
        } catch {
          return value
        }
      })()
    : '-'

  if (isEditing) {
    return (
      <Popover
        open
        onOpenChange={open => {
          if (!open) onCancel()
        }}
      >
        <PopoverTrigger asChild>
          <p className={READ_VALUE_CLASS}>{displayValue}</p>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={date => {
              if (date) {
                const iso = format(date, 'yyyy-MM-dd')
                onSave(iso)
              } else {
                onSave(null)
              }
            }}
            className="rounded-lg border"
          />
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <p
      onDoubleClick={isSaving ? undefined : onStartEdit}
      className={READ_VALUE_CLASS}
    >
      {displayValue}
      {isSaving && (
        <Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </p>
  )
}

// --- Hierarchy helpers ---

function findNodePath(
  nodes: HierarchyTreeNode[],
  targetId: string,
  path: HierarchyTreeNode[] = []
): HierarchyTreeNode[] {
  for (const node of nodes) {
    if (node.id === targetId) return [...path, node]
    if (node.children) {
      const found = findNodePath(node.children, targetId, [...path, node])
      if (found.length > 0) return found
    }
  }
  return []
}

interface HierarchyComboboxData {
  currentIds: Record<HierarchyLevel, string | null>
  getOptionsForLevel: (level: HierarchyLevel) => SelectOption[]
  isLoading: boolean
}

function useHierarchyComboboxData(
  plan: ApiPlanDetailResponse
): HierarchyComboboxData {
  const { data: tree, isLoading } = useQuery({
    queryKey: queryKeys.hierarchy.tree(),
    queryFn: fetchHierarchyTree,
    staleTime: 10 * 60 * 1000,
  })

  const result = useMemo(() => {
    if (!tree)
      return {
        currentIds: {
          DIVISION: null,
          DEPARTMENT: null,
          CLASS: null,
          SUBCLASS: null,
        },
        getOptionsForLevel: () => [],
      }

    // Build id → node map
    const idMap = new Map<string, HierarchyTreeNode>()
    function indexTree(nodes: HierarchyTreeNode[]) {
      for (const node of nodes) {
        idMap.set(node.id, node)
        if (node.children) indexTree(node.children)
      }
    }
    indexTree(tree)

    // Resolve current path
    const currentPath = plan.hierarchy_id
      ? findNodePath(tree, plan.hierarchy_id)
      : []

    const currentIds: Record<HierarchyLevel, string | null> = {
      DIVISION: currentPath.find(n => n.level === 'DIVISION')?.id ?? null,
      DEPARTMENT: currentPath.find(n => n.level === 'DEPARTMENT')?.id ?? null,
      CLASS: currentPath.find(n => n.level === 'CLASS')?.id ?? null,
      SUBCLASS: currentPath.find(n => n.level === 'SUBCLASS')?.id ?? null,
    }

    function getOptionsForLevel(level: HierarchyLevel): SelectOption[] {
      if (level === 'DIVISION') {
        return tree!
          .filter(n => n.level === 'DIVISION')
          .map(n => ({ value: n.id, label: n.name }))
      }

      // Determine parent ID for this level
      let parentId: string | null = null
      switch (level) {
        case 'DEPARTMENT':
          parentId = currentIds.DIVISION
          break
        case 'CLASS':
          parentId = currentIds.DEPARTMENT
          break
        case 'SUBCLASS':
          parentId = currentIds.CLASS
          break
      }

      if (!parentId) return []
      const parent = idMap.get(parentId)
      if (!parent?.children) return []
      return parent.children
        .filter(n => n.level === level)
        .map(n => ({ value: n.id, label: n.name }))
    }

    return { currentIds, getOptionsForLevel }
  }, [tree, plan.hierarchy_id])

  return { ...result, isLoading }
}

// --- EditableHierarchyComboboxField ---

interface EditableHierarchyComboboxFieldProps {
  value: string | null
  nodeId: string | null
  isEditing: boolean
  isSaving?: boolean
  onStartEdit: () => void
  onSave: (nodeId: string) => void
  onCancel: () => void
  options: SelectOption[]
  isLoadingOptions: boolean
}

function EditableHierarchyComboboxField({
  value,
  nodeId,
  isEditing,
  isSaving,
  onStartEdit,
  onSave,
  onCancel,
  options,
  isLoadingOptions,
}: EditableHierarchyComboboxFieldProps) {
  const [search, setSearch] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  useEffect(() => {
    if (!isEditing) setSearch('')
  }, [isEditing])

  const filtered = useMemo(() => {
    if (!search) return options
    const lower = search.toLowerCase()
    return options.filter(o => o.label.toLowerCase().includes(lower))
  }, [options, search])

  if (isEditing) {
    return (
      <Popover
        open
        onOpenChange={open => {
          if (!open) onCancel()
        }}
      >
        <PopoverTrigger asChild>
          <p className={READ_VALUE_CLASS}>{value || '-'}</p>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-0" align="start">
          <div className="p-2">
            <input
              ref={inputRef}
              className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm outline-none placeholder:text-muted-foreground"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto px-1 pb-1">
            {isLoadingOptions && (
              <div className="py-4 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            )}
            {!isLoadingOptions && filtered.length === 0 && (
              <div className="py-4 text-center text-sm text-muted-foreground">
                {options.length === 0
                  ? 'Select parent level first'
                  : 'No results found'}
              </div>
            )}
            {!isLoadingOptions &&
              filtered.map(option => {
                const isSelected = option.value === nodeId
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={cn(
                      'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
                      'hover:bg-accent hover:text-accent-foreground',
                      isSelected && 'bg-accent/50'
                    )}
                    onClick={() => onSave(option.value)}
                  >
                    <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
                      {isSelected && <Check className="h-4 w-4" />}
                    </span>
                    <span className="truncate">{option.label}</span>
                  </button>
                )
              })}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <p
      onDoubleClick={isSaving ? undefined : onStartEdit}
      className={READ_VALUE_CLASS}
    >
      {value || '-'}
      {isSaving && (
        <Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </p>
  )
}

// --- EditableSeasonField ---

interface EditableSeasonFieldProps {
  value: string | null
  isSaving?: boolean
  planId: string
}

function EditableSeasonField({
  value,
  isSaving,
  planId,
}: EditableSeasonFieldProps) {
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data: planCalendar } = usePlanCalendar(planId, true)

  return (
    <>
      <p
        onDoubleClick={isSaving ? undefined : () => setDialogOpen(true)}
        className={READ_VALUE_CLASS}
      >
        {value || '-'}
        {isSaving && (
          <Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
        )}
      </p>
      <CalendarEventPickerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        planId={planId}
        isSeasonal
        currentCalendarId={planCalendar?.calendar_id}
        currentEventId={planCalendar?.event_id}
      />
    </>
  )
}

// --- MetricsGrid ---

interface MetricsGridProps {
  plan: ApiPlanDetailResponse
  onSave: (fieldKey: string, value: string | number | null) => void
  savingFields?: Set<string>
}

export function MetricsGrid({ plan, onSave, savingFields }: MetricsGridProps) {
  const [editingField, setEditingField] = useState<string | null>(null)
  const [pendingHierarchyChange, setPendingHierarchyChange] = useState<{
    nodeId: string
    levelLabel: string
  } | null>(null)
  const hierarchyData = useHierarchyComboboxData(plan)
  const { data: pogGroupsData, isLoading: pogGroupsLoading } = usePogGroups()
  const pogGroupOptions = useMemo(
    () => (pogGroupsData?.values ?? []).map(g => ({ value: g, label: g })),
    [pogGroupsData]
  )

  const handleSave = (fieldKey: string, value: string | number | null) => {
    setEditingField(null)
    onSave(fieldKey, value)
  }

  const handleCancel = () => setEditingField(null)

  const handleHierarchyConfirm = () => {
    if (pendingHierarchyChange) {
      onSave('hierarchy_id', pendingHierarchyChange.nodeId)
      setPendingHierarchyChange(null)
    }
  }

  const isSavingHierarchy = savingFields?.has('hierarchy_id') ?? false

  const renderEditor = (field: FieldConfig) => {
    const value = field.getValue(plan)
    const isEditing = editingField === field.key
    const isSaving = savingFields?.has(field.key) ?? false
    const startEdit = () => {
      if (!isSaving && !isSavingHierarchy) setEditingField(field.key)
    }

    switch (field.type) {
      case 'text':
        return (
          <EditableTextField
            value={value != null ? String(value) : null}
            isEditing={isEditing}
            isSaving={isSaving}
            onStartEdit={startEdit}
            onSave={v => handleSave(field.key, v)}
            onCancel={handleCancel}
          />
        )

      case 'float':
        return (
          <EditableNumberField
            value={value}
            isEditing={isEditing}
            isSaving={isSaving}
            onStartEdit={startEdit}
            onSave={v => handleSave(field.key, v)}
            onCancel={handleCancel}
            step="0.01"
          />
        )

      case 'percent':
        return (
          <EditableNumberField
            value={value}
            isEditing={isEditing}
            isSaving={isSaving}
            onStartEdit={startEdit}
            onSave={v => handleSave(field.key, v)}
            onCancel={handleCancel}
            step="0.01"
            isPercent
          />
        )

      case 'integer':
        return (
          <EditableNumberField
            value={value}
            isEditing={isEditing}
            isSaving={isSaving}
            onStartEdit={startEdit}
            onSave={v => handleSave(field.key, v)}
            onCancel={handleCancel}
            step="1"
          />
        )

      case 'select': {
        const selectField = field as SelectFieldConfig
        return (
          <EditableSelectField
            value={value != null ? String(value) : null}
            isEditing={isEditing}
            isSaving={isSaving}
            onStartEdit={startEdit}
            onSave={v => handleSave(field.key, v)}
            onCancel={handleCancel}
            options={selectField.options}
          />
        )
      }

      case 'pogGroup':
        return (
          <EditableSelectField
            value={value != null ? String(value) : null}
            isEditing={isEditing}
            isSaving={isSaving}
            onStartEdit={startEdit}
            onSave={v => handleSave(field.key, v)}
            onCancel={handleCancel}
            options={pogGroupOptions}
            isLoadingOptions={pogGroupsLoading}
            allowClear
          />
        )

      case 'date':
        return (
          <EditableDateField
            value={value != null ? String(value) : null}
            isEditing={isEditing}
            isSaving={isSaving}
            onStartEdit={startEdit}
            onSave={v => handleSave(field.key, v)}
            onCancel={handleCancel}
          />
        )

      case 'hierarchy': {
        const hierarchyField = field as HierarchyFieldConfig
        const options = hierarchyData.getOptionsForLevel(
          hierarchyField.hierarchyLevel
        )
        const currentNodeId =
          hierarchyData.currentIds[hierarchyField.hierarchyLevel]
        return (
          <EditableHierarchyComboboxField
            value={value != null ? String(value) : null}
            nodeId={currentNodeId}
            isEditing={isEditing}
            isSaving={isSavingHierarchy}
            onStartEdit={startEdit}
            onSave={nodeId => {
              setEditingField(null)
              setPendingHierarchyChange({
                nodeId,
                levelLabel: field.label.toLowerCase(),
              })
            }}
            onCancel={handleCancel}
            options={options}
            isLoadingOptions={hierarchyData.isLoading}
          />
        )
      }

      case 'season':
        return (
          <EditableSeasonField
            value={value != null ? String(value) : null}
            isSaving={isSaving}
            planId={plan.id}
          />
        )
    }
  }

  return (
    <div className="space-y-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-[#101828]">
        Metrics & Attributes
      </h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        {FIELD_CONFIG.map(field => {
          const value = field.getValue(plan)

          return (
            <div key={field.key} className="space-y-1">
              <p className="text-xs text-muted-foreground">{field.label}</p>
              {field.editable ? (
                renderEditor(field)
              ) : (
                <p className="text-sm font-medium min-h-[1.75rem] flex items-center px-1 bg-[#f3f4f6] rounded">
                  {formatDisplayValue(value, field.type, field)}
                </p>
              )}
            </div>
          )
        })}
      </div>

      <ConfirmationDialog
        open={!!pendingHierarchyChange}
        onOpenChange={open => {
          if (!open) setPendingHierarchyChange(null)
        }}
        title={`Change ${pendingHierarchyChange?.levelLabel ?? ''}`}
        description={`This will update all dummy SKUs in the plan to the new ${pendingHierarchyChange?.levelLabel ?? ''}. Non-dummy SKUs will not be affected.`}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={handleHierarchyConfirm}
      />
    </div>
  )
}
